/**
 * LeftPanel.jsx
 * Standalone dark left panel — 60% width.
 * Static content, no props needed.
 */

import { Box, BarChart2, Stamp, MessageSquare } from "lucide-react";

/* ── Inject fonts once ────────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("auth-fonts")) {
  const l = document.createElement("link");
  l.id  = "auth-fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

const FEATURES = [
  { icon: Box,           title: "3D & 2D Design Editor",     desc: "Advanced design tools for precision engineering" },
  { icon: BarChart2,     title: "Auto Material Takeoff",     desc: "Automated calculations for efficient planning" },
  { icon: Stamp,         title: "PE-Stamped Blueprints",     desc: "Receive plans ready for licensed engineering sign-off" },
  { icon: MessageSquare, title: "Quote & Estimate Workflow", desc: "Streamlined pricing and project estimation" },
];

export default function LeftPanel() {
  return (
    <div style={{
      width: "50%",
      minHeight: "100vh",
      background: "#000000",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "60px 56px",
      position: "relative",
      overflow: "hidden",
      flexShrink: 0,
      boxSizing: "border-box",
    }}>

      {/* Blueprint grid overlay */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(rgba(54,76,96,0.1) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(54,76,96,0.1) 1px, transparent 1px)",
        backgroundSize: "36px 36px",
      }} />

      {/* Subtle glow — bottom left */}
      <div style={{
        position: "absolute", bottom: -180, left: -140, pointerEvents: "none",
        width: 520, height: 520, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 65%)",
      }} />

      {/* ── Main content (centered vertically) ── */}
      <div style={{
        position: "relative",
        zIndex: 1,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        textAlign: "center",
      }}>

        {/* Eyebrow */}
        <div style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.62rem",
          letterSpacing: "0.04em",
          color: "rgba(255,255,255,0.5)",
          marginBottom: 18,
        }}>
          Engineering Platform
        </div>

        {/* Platform name — Line 1 */}
        <h1 style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "clamp(2.4rem, 4vw, 4.2rem)",
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: "-0.01em",
          color: "#ffffff",
          margin: "0 0 10px 0",
        }}>
          MyDesignerPlus
        </h1>

        {/* Tagline — Line 2 */}
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "clamp(0.85rem, 1.1vw, 1rem)",
          fontWeight: 300,
          color: "rgba(255,255,255,0.35)",
          lineHeight: 1.6,
          margin: "0 0 48px 0",
          letterSpacing: "0.01em",
        }}>
          Design In 3D. Build With Engineered Plans.
        </p>

        {/* Feature cards — centered, constrained width */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "100%",
          maxWidth: 460,
        }}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 10,
              padding: "14px 18px",
              textAlign: "left",
            }}>
              <div style={{
                flexShrink: 0,
                width: 36, height: 36,
                borderRadius: 8,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 1,
              }}>
                <Icon size={16} style={{ color: "#ffffff" }} />
              </div>
              <div>
                <div style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "rgba(255,255,255,0.85)",
                  lineHeight: 1.3,
                  marginBottom: 3,
                }}>
                  {title}
                </div>
                <div style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.78rem",
                  color: "rgba(255,255,255,0.4)",
                  lineHeight: 1.55,
                }}>
                  {desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Copyright — pinned to bottom ── */}
      <div style={{
        position: "relative",
        zIndex: 1,
        marginTop: 40,
        textAlign: "center",
      }}>
        <span style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.6rem",
          letterSpacing: "0.08em",
          color: "#7a8692",
        }}>
          © 2025 MyDesignerPlus Inc.
        </span>
      </div>

    </div>
  );
}